import { useState, useMemo, useEffect, useRef } from 'react';
import { Check, ChevronsUpDown, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { LocationFormDialog } from '@/components/shared/LocationFormDialog';
import {
  formatLocationDisplay,
  getItemLabel,
  groupLocations,
  type LocationGroup,
} from '@/components/shared/location-utils';
import type { Location } from '@jobos/shared/types/location';

interface LocationComboboxProps {
  clientId: string;
  value: string;
  onValueChange: (locationId: string) => void;
  locations: Location[];
  disabled?: boolean;
  placeholder?: string;
  defaultManagerId?: string;
  onLocationCreated?: (location: Location) => void;
}

export function LocationCombobox({
  clientId,
  value,
  onValueChange,
  locations,
  disabled = false,
  placeholder = 'Select a service address',
  defaultManagerId,
  onLocationCreated,
}: LocationComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogDefaults, setAddDialogDefaults] = useState<{
    managerId?: string;
    addressLine1?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (buttonRef.current) {
      setWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  const groups = useMemo(() => groupLocations(locations), [locations]);
  const isSearching = search.trim().length > 0;

  const selectedLocation = locations.find((l) => l.id === value);
  const displayLabel = selectedLocation
    ? formatLocationDisplay(selectedLocation)
    : placeholder;

  // When popover opens, expand the group containing the current selection
  useEffect(() => {
    if (!open) return;
    const selectedGroup = groups.find((g) =>
      g.locations.some((l) => l.id === value)
    );
    setExpandedGroups(selectedGroup ? new Set([selectedGroup.key]) : new Set());
    setSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelect = (locationId: string) => {
    onValueChange(locationId);
    setOpen(false);
  };

  const handleAddUnit = (group: LocationGroup) => {
    const template = group.locations[0]!;
    setAddDialogDefaults({
      addressLine1: template.addressLine1 || '',
      city: template.city || '',
      state: template.state || '',
      zip: template.zip || '',
      managerId: template.managerId || '',
    });
    setOpen(false);
    setAddDialogOpen(true);
  };

  const handleAddNew = () => {
    setAddDialogDefaults(null);
    setOpen(false);
    setAddDialogOpen(true);
  };

  const handleLocationCreated = (location: Location) => {
    setAddDialogOpen(false);
    onLocationCreated?.(location);
  };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(isOpen: boolean) => {
          setOpen(isOpen);
          if (!isOpen) setSearch('');
        }}
      >
        <PopoverTrigger asChild>
          <Button
            ref={buttonRef}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
          >
            <span className="truncate">
              {selectedLocation ? displayLabel : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          data-ignore-deselect
          className="p-0"
          align="start"
          style={{ minWidth: width ? `${width}px` : undefined }}
        >
          <Command
            filter={(value: string, search: string) => {
              if (value.toLowerCase().includes(search.toLowerCase())) return 1;
              return 0;
            }}
          >
            <CommandInput
              placeholder="Search addresses..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {isSearching ? (
                <>
                  <CommandEmpty>No addresses found.</CommandEmpty>
                  {groups.map((group) => (
                    <CommandGroup key={group.key} heading={group.heading}>
                      {group.locations.map((location) => (
                        <CommandItem
                          key={location.id}
                          value={formatLocationDisplay(location)}
                          onSelect={() => handleSelect(location.id)}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4 shrink-0',
                              value === location.id
                                ? 'opacity-100'
                                : 'opacity-0'
                            )}
                          />
                          {getItemLabel(location)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </>
              ) : (
                groups.map((group) => {
                  const isExpanded = expandedGroups.has(group.key);
                  return (
                    <div key={group.key} className="px-1 py-0.5">
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.key)}
                        className="w-full flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      >
                        <ChevronRight
                          className={cn(
                            'h-3 w-3 shrink-0 transition-transform',
                            isExpanded && 'rotate-90'
                          )}
                        />
                        <span className="flex-1 text-left truncate">
                          {group.heading}
                        </span>
                        <span className="text-muted-foreground/60">
                          ({group.locations.length})
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="ml-2">
                          {group.locations.map((location) => (
                            <CommandItem
                              key={location.id}
                              value={formatLocationDisplay(location)}
                              onSelect={() => handleSelect(location.id)}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4 shrink-0',
                                  value === location.id
                                    ? 'opacity-100'
                                    : 'opacity-0'
                                )}
                              />
                              {getItemLabel(location)}
                            </CommandItem>
                          ))}
                          <button
                            type="button"
                            onClick={() => handleAddUnit(group)}
                            className="w-full flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          >
                            <Plus className="h-3 w-3" />
                            Add unit here
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </CommandList>
            {!isSearching && (
              <div className="border-t p-1">
                <button
                  type="button"
                  onClick={handleAddNew}
                  className="w-full flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                >
                  <Plus className="h-3 w-3" />
                  Add new location
                </button>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>

      <LocationFormDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={handleLocationCreated}
        clientId={clientId}
        defaultManagerId={defaultManagerId}
        defaultValues={addDialogDefaults}
      />
    </>
  );
}
